import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateProjectUnderOpportunity } from '@/hooks/useOpportunities';

interface AddProjectToOpportunityProps {
  opportunityId: string;
  opportunityTitle: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AddProjectToOpportunity({
  opportunityId,
  opportunityTitle,
  onClose,
  onSuccess,
}: AddProjectToOpportunityProps) {
  const navigate = useNavigate();
  const createProject = useCreateProjectUnderOpportunity();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    deliveryLocation: 'Remote' as 'Remote' | 'Onsite' | 'Hybrid' | 'Manual',
    engagementStartDate: '',
    daysPerWeek: 5,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await createProject.mutateAsync({
        opportunityId,
        name: formData.name,
        description: formData.description || undefined,
        deliveryLocation: formData.deliveryLocation,
        engagementStartDate: formData.engagementStartDate || undefined,
        daysPerWeek: formData.daysPerWeek,
      });

      onSuccess?.();
      onClose();

      // Navigate to the new project
      navigate(`/projects/${result.project.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-lg">
        <div className="dialog-header">
          <h2 className="dialog-title">Add Project</h2>
          <p className="text-sm text-gray-500 mt-1">
            Create a new project under "{opportunityTitle}"
          </p>
        </div>

        <form onSubmit={handleSubmit} className="dialog-body space-y-4">
          <div>
            <label className="label">Project Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., VCF Assessment Phase"
              className="input mt-1 w-full"
              autoFocus
            />
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Project description..."
              className="input mt-1 w-full"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Delivery Location</label>
              <select
                value={formData.deliveryLocation}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    deliveryLocation: e.target.value as 'Remote' | 'Onsite' | 'Hybrid' | 'Manual',
                  })
                }
                className="input mt-1 w-full"
              >
                <option value="Remote">Remote</option>
                <option value="Onsite">Onsite</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Manual">Manual</option>
              </select>
            </div>
            <div>
              <label className="label">Start Date</label>
              <input
                type="date"
                value={formData.engagementStartDate}
                onChange={(e) =>
                  setFormData({ ...formData, engagementStartDate: e.target.value })
                }
                className="input mt-1 w-full"
              />
            </div>
          </div>
        </form>

        <div className="dialog-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.name || createProject.isPending}
            className="btn-primary"
          >
            {createProject.isPending ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}
